import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { QuickActionsMenuComponent } from '../../../shared/components/quick-actions-menu/quick-actions-menu.component';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { UserApiService } from '../../users/services/user-api.service';
import { CrmUserResponse } from '../../users/models/user.model';
import { TicketsApiService } from '../services/tickets-api.service';
import {
  Ticket,
  TicketCommentResponse,
  TicketCommentType,
  TicketCsatResponseDto,
  TicketFollowerResponse,
  TicketLinkResponse,
  TicketMergedSummary,
  TicketSlaTrackingResponse
} from '../models/tickets.model';
import {
  TicketCategory,
  TicketPipeline,
  TicketPipelineStage,
  TicketPriority,
  TicketStatus
} from '../models/tickets-config.model';
import { getLinkTypeLabel, getOrigenLabel } from '../utils/ticket-format.util';
import { toAuditLogResponses } from '../utils/ticket-audit-timeline.util';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import {
  TicketActionType,
  TicketsActionsModalsComponent
} from '../tickets-actions-modals/tickets-actions-modals.component';

export type TicketDetailTab =
  | 'info'
  | 'conversation'
  | 'timeline'
  | 'sla'
  | 'csat'
  | 'links'
  | 'merged';

const AUDIT_PAGE_SIZE = 15;

/**
 * Sub-componente compartido por `tickets-detail-modal` y `tickets-detail`
 * (página completa). Contiene todas las tabs (Info, Conversación, Timeline,
 * SLA, CSAT, Vínculos) + Followers toggle.
 *
 * No incluye el header con avatar/badges — eso lo decide cada wrapper para
 * preservar las diferencias entre modal y página.
 *
 * Inputs:
 *   - `ticket` y catálogos para mappear ids → labels.
 *   - `showHeader` (default true): muestra el header reusable. Pásalo en false
 *     si el wrapper renderiza su propio header.
 */
@Component({
  selector: 'app-tickets-detail-tabs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BadgeComponent,
    QuickActionsMenuComponent,
    EntitySelectComponent,
    HasPermissionDirective,
    TicketsActionsModalsComponent,
    AuditTimelineComponent
  ],
  templateUrl: './tickets-detail-tabs.component.html'
})
export class TicketsDetailTabsComponent implements OnChanges, OnDestroy {
  @Input() ticket: Ticket | null = null;

  @Input() priorities: TicketPriority[] = [];
  @Input() statuses: TicketStatus[] = [];
  @Input() categories: TicketCategory[] = [];
  @Input() pipelines: TicketPipeline[] = [];
  @Input() stages: TicketPipelineStage[] = [];

  /** Cuando es true, muestra header con avatar + ticketNumber + badges + estrella. */
  @Input() showHeader = true;

  @Output() closeRequest = new EventEmitter<void>();
  /** Se emite al disparar una acción rápida (correo/tarea/ticket/reunión/llamada).
   *  El wrapper modal lo usa para cerrarse y que el drawer (z-drawer) no quede
   *  atrapado debajo del modal (z-modal). En la página completa no se enlaza. */
  @Output() quickAction = new EventEmitter<void>();
  /** Se emite cuando se crea un sub-ticket desde la pestaña "Vínculos".
   *  El wrapper (página o drawer) decide navegar al hijo recién creado. */
  @Output() subTicketCreated = new EventEmitter<Ticket>();

  activeTab: TicketDetailTab = 'info';

  /** Controla el modal de creación de sub-ticket alojado en esta pestaña. */
  subTicketAction: TicketActionType | null = null;

  // Estado por tab (lazy-load on demand)
  comments: TicketCommentResponse[] = [];
  loadingComments = false;
  commentError = '';

  newCommentBody = '';
  newCommentType: TicketCommentType = 'INTERNAL';
  publishingComment = false;
  /** Menciones seleccionadas para el comentario nuevo (INC 18). */
  newCommentMentions: { id: number; name: string }[] = [];

  // Edición de comentario propio (INC 18)
  editingCommentId: number | null = null;
  editCommentBody = '';
  editCommentType: TicketCommentType = 'INTERNAL';
  editCommentMentions: { id: number; name: string }[] = [];
  savingEditComment = false;

  /** Cache de usuarios activos del negocio (para menciones y seguidores). */
  private allUsers: CrmUserResponse[] = [];

  // Timeline: entradas ya adaptadas al DTO del componente compartido
  // <app-audit-timeline> (ver `ticket-audit-timeline.util.ts`).
  auditLog: AuditLogResponse[] = [];       // slice de la página actual
  private auditAll: AuditLogResponse[] = []; // todas las entradas (backend no pagina)
  private auditLoaded = false;
  auditPage = 0;
  auditTotalPages = 0;
  auditTotalElements = 0;
  loadingAudit = false;
  auditError = '';

  sla: TicketSlaTrackingResponse | null = null;
  loadingSla = false;
  slaError = '';
  private slaInterval: ReturnType<typeof setInterval> | null = null;
  slaTickerNow = Date.now();

  csat: TicketCsatResponseDto | null = null;
  loadingCsat = false;
  csatError = '';

  followers: TicketFollowerResponse[] = [];
  loadingFollowers = false;
  followerActionInFlight = false;

  links: TicketLinkResponse[] = [];
  loadingLinks = false;

  /** Inc 52: tickets fusionados en este (GET /api/tickets/{id}/merged). */
  merged: TicketMergedSummary[] = [];
  loadingMerged = false;

  /** Inc 73: ticket padre (sólo cuando el ticket mostrado es un subticket). */
  parentTicket: Ticket | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private ticketsApi: TicketsApiService,
    private authService: AuthService,
    private userApi: UserApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private router: Router
  ) {}

  /**
   * Inc 25 — abre el DETALLE del lead de origen del ticket (no su edición). El
   * detalle de un lead es un modal sobre el kanban, por eso se navega a
   * /leads?openLead=<id>, igual que `verTicketPadre` con ?openTicket. Emite
   * `quickAction` para que el wrapper modal se cierre antes de navegar.
   */
  verLead(idLead: number | null): void {
    if (idLead == null) return;
    this.quickAction.emit();
    this.router.navigate(['/leads'], { queryParams: { openLead: idLead } });
  }

  /**
   * Inc 73 — true cuando el ticket mostrado es un subticket (tiene padre). En ese
   * caso no se muestra la pestaña "Vínculos" ni sus acciones.
   */
  get esSubTicket(): boolean {
    return this.ticket?.idTicketParent != null;
  }

  /** Etiqueta legible del ticket padre (cae al id si aún no se cargó). */
  get parentLabel(): string {
    if (this.parentTicket) {
      return `${this.parentTicket.ticketNumber} — ${this.parentTicket.subject}`;
    }
    return `Ticket #${this.ticket?.idTicketParent}`;
  }

  /**
   * Inc 73 — abre el detalle del ticket padre. El detalle es un modal sobre la
   * lista, por eso se navega a /tickets?openTicket=<id>. Emite `quickAction` para
   * que el wrapper modal se cierre antes de navegar (igual que `verLead`).
   */
  verTicketPadre(): void {
    const parentId = this.ticket?.idTicketParent;
    if (parentId == null) return;
    this.quickAction.emit();
    this.router.navigate(['/tickets'], { queryParams: { openTicket: parentId } });
  }

  /** Inc 73: carga el padre para mostrar su número/asunto en "Asociaciones". */
  private cargarPadre(): void {
    this.parentTicket = null;
    const parentId = this.ticket?.idTicketParent;
    if (parentId == null) return;
    this.ticketsApi
      .getTicketById(parentId)
      .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe((p) => (this.parentTicket = p));
  }

  // ===== Helpers legibles =====
  origenLabel(source: string | null | undefined): string {
    return getOrigenLabel(source);
  }

  linkTypeLabel(linkType: string | null | undefined): string {
    return getLinkTypeLabel(linkType);
  }

  get currentUserId(): number | null {
    return this.authService.getCurrentUser()?.id ?? null;
  }

  // ===== Búsqueda de usuarios (menciones / seguidores) =====
  userSearch = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const filt = (list: CrmUserResponse[]): EntitySelectPage<CrmUserResponse> => {
      const t = term.trim().toLowerCase();
      const items = t
        ? list.filter((u) =>
            `${u.fullname} ${u.lastname ?? ''} ${u.mail}`.toLowerCase().includes(t)
          )
        : list;
      return { items, totalPages: 1 };
    };
    if (this.allUsers.length) {
      return of(filt(this.allUsers));
    }
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.userApi.listActiveByBusiness(businessId).pipe(
      map((users) => {
        this.allUsers = users.filter((u) => u.isActive !== false);
        return filt(this.allUsers);
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 }))
    );
  };

  userLabel = (u: CrmUserResponse): string =>
    `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticket']) {
      const prev = changes['ticket'].previousValue as Ticket | null;
      const cur = changes['ticket'].currentValue as Ticket | null;
      const idChanged = (prev?.id ?? null) !== (cur?.id ?? null);
      if (idChanged) {
        this.resetCaches();
        if (cur?.id) {
          // Followers se prefetch para que la estrella tenga estado correcto
          this.cargarFollowers();
          // Inc 73: padre para la sección "Asociaciones".
          this.cargarPadre();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.stopSlaTicker();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Tab switching =====
  onTabChange(tab: TicketDetailTab): void {
    // Inc 73: la pestaña "Vínculos" no existe para un subticket.
    if (tab === 'links' && this.esSubTicket) return;
    this.activeTab = tab;
    if (!this.ticket?.id) return;

    if (tab === 'conversation' && this.comments.length === 0 && !this.loadingComments) {
      this.cargarComments();
    } else if (tab === 'timeline' && !this.auditLoaded && !this.loadingAudit) {
      this.cargarAudit(0);
    } else if (tab === 'sla' && !this.sla && !this.loadingSla) {
      this.cargarSla();
    } else if (tab === 'csat' && !this.csat && !this.loadingCsat) {
      this.cargarCsat();
    } else if (tab === 'links' && this.links.length === 0 && !this.loadingLinks) {
      this.cargarLinks();
    } else if (tab === 'merged' && this.merged.length === 0 && !this.loadingMerged) {
      this.cargarMerged();
    }

    if (tab !== 'sla') {
      this.stopSlaTicker();
    }
  }

  private resetCaches(): void {
    this.activeTab = 'info';
    this.comments = [];
    this.loadingComments = false;
    this.commentError = '';
    this.newCommentBody = '';
    this.newCommentType = 'INTERNAL';
    this.publishingComment = false;
    this.newCommentMentions = [];
    this.editingCommentId = null;
    this.editCommentBody = '';
    this.editCommentMentions = [];
    this.savingEditComment = false;
    this.auditLog = [];
    this.auditAll = [];
    this.auditLoaded = false;
    this.auditPage = 0;
    this.auditTotalPages = 0;
    this.auditTotalElements = 0;
    this.loadingAudit = false;
    this.auditError = '';
    this.sla = null;
    this.loadingSla = false;
    this.slaError = '';
    this.stopSlaTicker();
    this.csat = null;
    this.loadingCsat = false;
    this.csatError = '';
    this.followers = [];
    this.loadingFollowers = false;
    this.followerActionInFlight = false;
    this.links = [];
    this.loadingLinks = false;
    this.merged = [];
    this.loadingMerged = false;
    this.parentTicket = null;
  }

  // ===== Comments =====
  cargarComments(): void {
    if (!this.ticket?.id) return;
    this.loadingComments = true;
    this.commentError = '';
    this.ticketsApi
      .getComments(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.comments = data;
          this.loadingComments = false;
        },
        error: (err) => {
          this.loadingComments = false;
          this.commentError = err.error?.message || 'Error al cargar la conversación';
        }
      });
  }

  publicarComentario(): void {
    if (!this.ticket?.id) return;
    const body = (this.newCommentBody || '').trim();
    if (!body) return;

    this.publishingComment = true;
    this.commentError = '';
    this.ticketsApi
      .createComment(this.ticket.id, {
        commentType: this.newCommentType,
        bodyHtml: body, // Fase 3: sin editor rich text — sólo texto plano
        bodyText: body,
        mentionedUserIds: this.newCommentMentions.map((m) => m.id)
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created) => {
          this.publishingComment = false;
          this.comments = [...this.comments, created];
          this.newCommentBody = '';
          this.newCommentMentions = [];
          this.toast.success('Comentario publicado');
        },
        error: (err) => {
          this.publishingComment = false;
          const msg = err.error?.message || 'Error al publicar el comentario';
          this.commentError = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      });
  }

  // ===== Menciones (comentario nuevo) =====
  addMention(user: CrmUserResponse | null): void {
    if (!user) return;
    if (!this.newCommentMentions.some((m) => m.id === user.id)) {
      this.newCommentMentions.push({ id: user.id, name: this.userLabel(user) });
    }
  }

  removeMention(id: number): void {
    this.newCommentMentions = this.newCommentMentions.filter((m) => m.id !== id);
  }

  // ===== Editar / eliminar comentario propio (INC 18) =====
  esComentarioPropio(c: TicketCommentResponse): boolean {
    return c.intIdUserAuthor != null && c.intIdUserAuthor === this.currentUserId;
  }

  iniciarEdicion(c: TicketCommentResponse): void {
    this.editingCommentId = c.id;
    this.editCommentBody = c.bodyText;
    this.editCommentType = c.commentType;
    this.editCommentMentions = (c.mentionedUserIds || []).map((id, i) => ({
      id,
      name: c.mentionedUserNames?.[i] || 'Usuario sin nombre'
    }));
  }

  cancelarEdicion(): void {
    this.editingCommentId = null;
    this.editCommentBody = '';
    this.editCommentMentions = [];
    this.savingEditComment = false;
  }

  addEditMention(user: CrmUserResponse | null): void {
    if (!user) return;
    if (!this.editCommentMentions.some((m) => m.id === user.id)) {
      this.editCommentMentions.push({ id: user.id, name: this.userLabel(user) });
    }
  }

  removeEditMention(id: number): void {
    this.editCommentMentions = this.editCommentMentions.filter((m) => m.id !== id);
  }

  guardarEdicion(c: TicketCommentResponse): void {
    if (!this.ticket?.id) return;
    const body = (this.editCommentBody || '').trim();
    if (!body) return;
    this.savingEditComment = true;
    this.ticketsApi
      .updateComment(this.ticket.id, c.id, {
        commentType: this.editCommentType,
        bodyHtml: body,
        bodyText: body,
        mentionedUserIds: this.editCommentMentions.map((m) => m.id)
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.savingEditComment = false;
          this.comments = this.comments.map((x) => (x.id === updated.id ? updated : x));
          this.cancelarEdicion();
          this.toast.updated('Comentario');
        },
        error: (err) => {
          this.savingEditComment = false;
          const msg = err.error?.message || 'No se pudo editar el comentario';
          this.toast.error({ title: 'Error', message: msg });
        }
      });
  }

  async eliminarComentario(c: TicketCommentResponse): Promise<void> {
    if (!this.ticket?.id) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar comentario',
      message: '¿Seguro que desea eliminar este comentario? Esta acción no se puede deshacer.'
    });
    if (!ok) return;
    this.ticketsApi
      .deleteComment(this.ticket.id, c.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.comments = this.comments.filter((x) => x.id !== c.id);
          this.toast.deleted('Comentario');
        },
        error: (err) => {
          const msg = err.error?.message || 'No se pudo eliminar el comentario';
          this.toast.error({ title: 'Error', message: msg });
        }
      });
  }

  // ===== Audit log (timeline) =====
  // Fuente: el audit log PROPIO del ticket (GET /api/tickets/{id}/audit), que
  // registra eventos que la auditoría central no tiene (SLA_ALERT, SPLIT, MERGE,
  // COMMENT*, FOLLOWER_*...). Las entradas se adaptan al DTO de
  // <app-audit-timeline> para homologar la presentación con el resto de módulos.
  // El backend devuelve TODAS las entradas en un array (sin paginar); se cargan
  // una sola vez y la paginación se resuelve en cliente.
  cargarAudit(page: number): void {
    if (!this.ticket?.id) return;
    if (this.auditLoaded) {
      this.aplicarAuditPage(page);
      return;
    }
    this.loadingAudit = true;
    this.auditError = '';
    this.ticketsApi
      .getAuditLog(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.auditAll = toAuditLogResponses(data);
          this.auditLoaded = true;
          this.auditTotalElements = this.auditAll.length;
          this.auditTotalPages = Math.max(1, Math.ceil(this.auditAll.length / AUDIT_PAGE_SIZE));
          this.aplicarAuditPage(page);
          this.loadingAudit = false;
        },
        error: (err) => {
          this.loadingAudit = false;
          this.auditError = err.error?.message || 'Error al cargar la línea de tiempo';
        }
      });
  }

  private aplicarAuditPage(page: number): void {
    const last = Math.max(0, this.auditTotalPages - 1);
    const clamped = Math.min(Math.max(0, page), last);
    this.auditPage = clamped;
    const start = clamped * AUDIT_PAGE_SIZE;
    this.auditLog = this.auditAll.slice(start, start + AUDIT_PAGE_SIZE);
  }

  // La presentación de los eventos (titulares, traducciones, diffs, badges,
  // expand/colapso y paginación) vive en AuditTimelineComponent /
  // AuditDisplayService; la adaptación del DTO, en ticket-audit-timeline.util.

  // ===== SLA =====
  cargarSla(): void {
    if (!this.ticket?.id) return;
    this.loadingSla = true;
    this.slaError = '';
    this.ticketsApi
      .getSlaTracking(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.sla = data;
          this.loadingSla = false;
          this.startSlaTicker();
        },
        error: (err) => {
          this.loadingSla = false;
          // 404 cuando no hay política aplicada — UI muestra mensaje, no error.
          if (err.status === 404) {
            this.sla = null;
            this.slaError = '';
          } else {
            this.slaError = err.error?.message || 'Error al cargar SLA';
          }
        }
      });
  }

  private startSlaTicker(): void {
    this.stopSlaTicker();
    this.slaTickerNow = Date.now();
    this.slaInterval = setInterval(() => {
      this.slaTickerNow = Date.now();
    }, 60_000);
  }

  private stopSlaTicker(): void {
    if (this.slaInterval) {
      clearInterval(this.slaInterval);
      this.slaInterval = null;
    }
  }

  getMinutesRemaining(dueAtIso: string | null): number | null {
    if (!dueAtIso) return null;
    const due = new Date(dueAtIso).getTime();
    if (Number.isNaN(due)) return null;
    return Math.round((due - this.slaTickerNow) / 60_000);
  }

  formatMinutes(min: number | null | undefined): string {
    if (min == null) return '—';
    const sign = min < 0 ? '-' : '';
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `${sign}${h}h ${m}m`;
  }

  /**
   * Color del badge SLA. Inc 71: se colorea según el estado agregado
   * autoritativo del backend (`sla.status`); si un backend antiguo no lo envía,
   * se cae al cálculo por flags.
   */
  get slaStateColor(): string {
    if (!this.sla) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    switch (this.sla.status) {
      case 'MET':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'BREACHED':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      case 'AT_RISK':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'PAUSED':
        return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    }
    // Fallback (backend sin `status`): derivar de los flags.
    if (this.sla.isPaused) return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    if (this.sla.isBreachedResponse || this.sla.isBreachedResolution)
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    if (this.sla.warningResponseSent || this.sla.warningResolutionSent)
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
    if (this.sla.firstResponseMet && this.sla.resolutionMet)
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
  }

  get slaStateLabel(): string {
    if (!this.sla) return 'Sin política';
    // Inc 71: usar la etiqueta autoritativa del backend cuando exista.
    if (this.sla.statusLabel && this.sla.statusLabel.trim()) return this.sla.statusLabel;
    // Fallback (backend sin `statusLabel`): derivar de los flags.
    if (this.sla.isPaused) return 'Pausado';
    if (this.sla.isBreachedResponse || this.sla.isBreachedResolution) return 'Vencido';
    if (this.sla.warningResponseSent || this.sla.warningResolutionSent) return 'En riesgo';
    if (this.sla.firstResponseMet && this.sla.resolutionMet) return 'Cumplido';
    return 'En curso';
  }

  // ===== CSAT =====
  cargarCsat(): void {
    if (!this.ticket?.id) return;
    this.loadingCsat = true;
    this.csatError = '';
    this.ticketsApi
      .getCsat(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.csat = data;
          this.loadingCsat = false;
        },
        error: (err) => {
          this.loadingCsat = false;
          if (err.status !== 404) {
            this.csatError = err.error?.message || 'Error al cargar CSAT';
          }
        }
      });
  }

  // ===== Followers =====
  cargarFollowers(): void {
    if (!this.ticket?.id) return;
    this.loadingFollowers = true;
    this.ticketsApi
      .getFollowers(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.followers = data;
          this.loadingFollowers = false;
        },
        error: () => {
          this.loadingFollowers = false;
        }
      });
  }

  toggleFollow(): void {
    if (!this.ticket?.id || this.followerActionInFlight) return;
    const userId = this.authService.getCurrentUser()?.id;
    if (userId == null) return;

    this.followerActionInFlight = true;
    const isFollowing = this.followers.some((f) => f.intIdUser === userId);

    const obs: Observable<unknown> = isFollowing
      ? this.ticketsApi.removeFollower(this.ticket.id, userId)
      : this.ticketsApi.addFollower(this.ticket.id, { intIdUser: userId });

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.followerActionInFlight = false;
        this.cargarFollowers();
      },
      error: (err: unknown) => {
        this.followerActionInFlight = false;
        const message =
          (err as { error?: { message?: string } })?.error?.message ||
          'No se pudo actualizar seguidores';
        this.toast.error({ title: 'Error', message });
      }
    });
  }

  isFollowing(): boolean {
    const userId = this.authService.getCurrentUser()?.id;
    if (userId == null) return false;
    return this.followers.some((f) => f.intIdUser === userId);
  }

  /** Agrega cualquier usuario como seguidor (INC 21). */
  agregarSeguidor(user: CrmUserResponse | null): void {
    if (!user || !this.ticket?.id || this.followerActionInFlight) return;
    if (this.followers.some((f) => f.intIdUser === user.id)) return;
    this.followerActionInFlight = true;
    this.ticketsApi
      .addFollower(this.ticket.id, { intIdUser: user.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.followerActionInFlight = false;
          this.cargarFollowers();
        },
        error: (err: unknown) => {
          this.followerActionInFlight = false;
          const message =
            (err as { error?: { message?: string } })?.error?.message ||
            'No se pudo agregar el seguidor';
          this.toast.error({ title: 'Error', message });
        }
      });
  }

  /** Quita un seguidor por su id de usuario (INC 21). */
  quitarSeguidor(userId: number): void {
    if (!this.ticket?.id || this.followerActionInFlight) return;
    this.followerActionInFlight = true;
    this.ticketsApi
      .removeFollower(this.ticket.id, userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.followerActionInFlight = false;
          this.cargarFollowers();
        },
        error: (err: unknown) => {
          this.followerActionInFlight = false;
          const message =
            (err as { error?: { message?: string } })?.error?.message ||
            'No se pudo quitar el seguidor';
          this.toast.error({ title: 'Error', message });
        }
      });
  }

  followerName(f: TicketFollowerResponse): string {
    return f.userName || 'Usuario sin nombre';
  }

  // ===== Links =====
  cargarLinks(): void {
    if (!this.ticket?.id) return;
    this.loadingLinks = true;
    this.ticketsApi
      .getLinks(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.links = data;
          this.loadingLinks = false;
        },
        error: () => {
          this.loadingLinks = false;
        }
      });
  }

  // ===== Merged (Inc 52) — tickets fusionados en este =====
  cargarMerged(): void {
    if (!this.ticket?.id) return;
    this.loadingMerged = true;
    this.ticketsApi
      .getMergedTickets(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.merged = data;
          this.loadingMerged = false;
        },
        error: () => {
          this.loadingMerged = false;
        }
      });
  }

  // ===== Sub-ticket (desde la pestaña Vínculos) =====
  abrirSubTicket(): void {
    if (!this.ticket?.id) return;
    // Inc 73: un subticket no puede tener subtickets.
    if (this.esSubTicket) return;
    this.subTicketAction = 'sub-ticket';
  }

  cerrarSubTicket(): void {
    this.subTicketAction = null;
  }

  onSubTicketCreado(child: Ticket | null): void {
    this.subTicketAction = null;
    if (!child) return;
    // Refrescar la lista de vínculos y asegurarnos de estar en la pestaña.
    this.activeTab = 'links';
    this.refrescarTrasSubTicket();
    this.subTicketCreated.emit(child);
  }

  /**
   * Inc 74: tras crear un subticket desde el padre, refresca los vínculos y
   * recarga la línea de tiempo (GET /api/tickets/{id}/audit) para que el evento
   * de creación del subticket aparezca sin recargar la página. También lo llaman
   * los wrappers (página y modal) cuando el subticket se crea desde su toolbar.
   */
  refrescarTrasSubTicket(): void {
    if (!this.ticket?.id) return;
    this.cargarLinks();
    this.auditLoaded = false;
    this.auditAll = [];
    this.auditLog = [];
    this.cargarAudit(0);
  }

  // ===== Header / iniciales =====
  // (El botón "Ver completo" se eliminó — INC 43. El modal de detalle es la vista única.)
  get initials(): string {
    if (!this.ticket?.subject) return '?';
    const words = this.ticket.subject.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }

  get avatarColorClass(): string {
    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    ];
    if (!this.ticket?.subject) return colors[0];
    const idx = this.ticket.subject.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[idx % colors.length];
  }

  // ===== Mappers de catálogos =====
  getPriorityLabel(idPriority: number | null): string {
    if (idPriority == null) return '-';
    return this.priorities.find((p) => p.id === idPriority)?.label || '-';
  }

  getStatusLabel(idStatus: number | null): string {
    if (idStatus == null) return '-';
    return this.statuses.find((s) => s.id === idStatus)?.label || '-';
  }

  getCategoryName(idCategory: number | null): string {
    if (idCategory == null) return '-';
    return this.categories.find((c) => c.id === idCategory)?.name || '-';
  }

  getPipelineName(idPipeline: number | null): string {
    if (idPipeline == null) return '-';
    return this.pipelines.find((p) => p.id === idPipeline)?.name || '-';
  }

  getStageName(idStage: number | null): string {
    if (idStage == null) return '-';
    return this.stages.find((s) => s.id === idStage)?.name || '-';
  }

  getPriorityColor(idPriority: number | null): 'success' | 'warning' | 'error' | 'info' {
    if (idPriority == null) return 'info';
    const level = this.priorities.find((p) => p.id === idPriority)?.level;
    if (level === 1 || level === 2) return 'error';
    if (level === 3) return 'warning';
    if (level === 4) return 'success';
    return 'info';
  }

  getStatusColor(idStatus: number | null): 'success' | 'warning' | 'error' | 'info' {
    if (idStatus == null) return 'info';
    const status = this.statuses.find((s) => s.id === idStatus);
    if (!status) return 'info';
    if (status.isResolved) return 'success';
    if (status.isTerminal) return 'error';
    return 'warning';
  }
}
